import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Divider,
  TextField,
  InputAdornment,
  Paper,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import InfoIcon from '@mui/icons-material/Info';
import DescriptionIcon from '@mui/icons-material/Description';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import PageContainer from '../components/shared/pageContainer';
import PageHeader from '../components/shared/page-header';
import { useManagementUnitsController } from './context/management-units-controller';
import { useManagementUnitsState } from './context/management-units-context';

export function ManagementUnitsComponent() {
  const { loadData } = useManagementUnitsController();
  const { units, loading, error } = useManagementUnitsState();
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  // Filtra as unidades baseado no termo de busca aplicado
  const filteredUnits = useMemo(() => {
    if (!appliedSearchTerm.trim()) {
      return units;
    }

    const term = appliedSearchTerm.toLowerCase();
    return units.filter((unit) => {
      const name = unit.name?.toLowerCase() || '';
      const operation = unit.operation?.toLowerCase() || '';
      const othersInfo = unit.othersInformation?.toLowerCase() || '';

      return (
        name.includes(term) ||
        operation.includes(term) ||
        othersInfo.includes(term)
      );
    });
  }, [units, appliedSearchTerm]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleSearch = () => {
    setAppliedSearchTerm(searchTerm);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setAppliedSearchTerm('');
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <Container maxWidth="xl">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 400,
            }}
          >
            <CircularProgress size={60} />
          </Box>
        </Container>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <Container maxWidth="xl">
          <PageHeader
            title="Estrutura Administrativa"
            subtitle="Conheça as unidades de gestão do município"
            breadcrumbs={[
              { label: 'Estrutura Administrativa' },
            ]}
          />
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        </Container>
      </PageContainer>
    );
  }

  if (units.length === 0) {
    return (
      <PageContainer>
        <Container maxWidth="xl">
          <PageHeader
            title="Estrutura Administrativa"
            subtitle="Conheça as unidades de gestão do município"
            breadcrumbs={[
              { label: 'Estrutura Administrativa' },
            ]}
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            Nenhuma unidade de gestão cadastrada no momento.
          </Alert>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container maxWidth="xl">
        <PageHeader
          title="Estrutura Administrativa"
          subtitle="Conheça as unidades de gestão do município"
          breadcrumbs={[
            { label: 'Estrutura Administrativa' },
          ]}
        />

        {/* Filtros de Pesquisa */}
        <Paper
          elevation={2}
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: 'background.paper',
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 2,
            }}
          >
            <FilterListIcon color="primary" />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
              }}
            >
              Filtros de Pesquisa
            </Typography>
          </Box>

          <TextField
            fullWidth
            label="Pesquisar"
            placeholder="Buscar por nome, operação ou informações"
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
            variant="outlined"
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              mt: 2,
            }}
          >
            <Button
              variant="contained"
              color="primary"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={loading}
              sx={{
                minWidth: 140,
              }}
            >
              Buscar
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              disabled={loading}
              sx={{
                minWidth: 140,
              }}
            >
              Limpar Filtros
            </Button>
          </Box>

          {appliedSearchTerm && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {filteredUnits.length} {filteredUnits.length === 1 ? 'resultado encontrado' : 'resultados encontrados'}
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Resultados */}
        {filteredUnits.length === 0 ? (
          <Alert severity="info">
            {appliedSearchTerm
              ? `Nenhuma unidade de gestão encontrada com o termo "${appliedSearchTerm}".`
              : 'Nenhuma unidade de gestão cadastrada no momento.'}
          </Alert>
        ) : (
          <>
            <Grid container spacing={3}>
              {filteredUnits.map((unit) => (
                <Grid item xs={12} key={unit.id}>
                  <Card
                    elevation={3}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 6,
                      },
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      {/* Cabeçalho */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          mb: 2,
                          pb: 2,
                          borderBottom: '2px solid',
                          borderColor: 'primary.main',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            backgroundColor: 'primary.main',
                            color: 'white',
                          }}
                        >
                          <BusinessIcon sx={{ fontSize: 28 }} />
                        </Box>
                        <Typography
                          variant="h6"
                          component="h3"
                          sx={{
                            fontWeight: 600,
                            color: 'primary.main',
                            flex: 1,
                          }}
                        >
                          {unit.name || 'Sem nome'}
                        </Typography>
                      </Box>

                      {/* Operação */}
                      {unit.operation && (
                        <Box sx={{ mb: 2 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 1,
                            }}
                          >
                            <InfoIcon color="action" fontSize="small" />
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 600, color: 'text.secondary' }}
                            >
                              Operação
                            </Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              pl: 3,
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {unit.operation}
                          </Typography>
                        </Box>
                      )}

                      {/* Outras Informações */}
                      {unit.othersInformation && (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <Box>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 1,
                              }}
                            >
                              <DescriptionIcon color="action" fontSize="small" />
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600, color: 'text.secondary' }}
                              >
                                Informações Adicionais
                              </Typography>
                            </Box>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                pl: 3,
                                whiteSpace: 'pre-wrap',
                              }}
                            >
                              {unit.othersInformation}
                            </Typography>
                          </Box>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Container>
    </PageContainer>
  );
}
